import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { getCurrentEmployee } from '../../services/pinAuthService';
import { getRedemptionStats, getRedemptionHistory, getEmployeeBalance, allocateComps, revokeComps } from '../../services/simpleCompService';
import { getEmployees, getEmployeesWithBalances, updateEmployee, resetEmployeePIN, deleteEmployee } from '../../services/simpleEmployeeService';
import { CreateUserModal } from './CreateUserModal';
import styles from './AdminDashboard.module.css';

export const AdminDashboard: React.FC = () => {
  const admin = getCurrentEmployee();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', is_admin: false, drinks_available: 0, food_available: 0 });
  const [newPin, setNewPin] = useState<string | null>(null);
  const [showPin, setShowPin] = useState(false);
  const [allocatingTo, setAllocatingTo] = useState<string | null>(null);
  const [allocateForm, setAllocateForm] = useState({ drinks: 2, food: 1, expiresIn: '7' });
  const [revokingFrom, setRevokingFrom] = useState<string | null>(null);
  const [revokeForm, setRevokeForm] = useState({ drinks: 0, food: 0 });

  const { data: statsResult, isLoading: statsLoading } = useQuery({
    queryKey: ['comp-stats'],
    queryFn: getRedemptionStats,
  });

  const { data: redemptionsResult, isLoading: redemptionsLoading } = useQuery({
    queryKey: ['recent-redemptions'],
    queryFn: () => getRedemptionHistory(undefined, 5),
  });

  const { data: employeesResult, isLoading: employeesLoading } = useQuery({
    queryKey: ['employees-with-balances'],
    queryFn: getEmployeesWithBalances,
  });

  const stats = statsResult?.data;
  const redemptions = redemptionsResult?.data || [];
  const employees = employeesResult?.data || [];
  const revokingEmployee = employees.find((employee) => employee.id === revokingFrom);

  const refetchEmployeesWithBalances = () =>
    queryClient.refetchQueries({ queryKey: ['employees-with-balances'] });

  const handleUserCreated = () => {
    queryClient.invalidateQueries({ queryKey: ['comp-stats'] });
    refetchEmployeesWithBalances();
  };

  // Update employee mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: any }) =>
      updateEmployee(id, updates),
    onSuccess: () => {
      refetchEmployeesWithBalances();
      setEditingEmployee(null);
    },
  });

  // Reset PIN mutation
  const resetPinMutation = useMutation({
    mutationFn: (id: string) => resetEmployeePIN(id),
    onSuccess: (data) => {
      refetchEmployeesWithBalances();
      setNewPin(data.pin || null);
      setShowPin(true);
    },
  });

  // Delete employee mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteEmployee(id),
    onSuccess: () => {
      refetchEmployeesWithBalances();
    },
  });

  // Allocate comps mutation
  const allocateMutation = useMutation({
    mutationFn: ({ employeeId, drinks, food, expiresAt }: any) =>
      allocateComps(employeeId, drinks, food, expiresAt),
    onSuccess: () => {
      console.log('Allocation successful, invalidating queries');
      queryClient.invalidateQueries({ queryKey: ['employee-balance'] });
      refetchEmployeesWithBalances();
      setAllocatingTo(null);
      setAllocateForm({ drinks: 2, food: 1, expiresIn: '7' });
      alert('Comps allocated successfully');
    },
    onError: (error: any) => {
      console.error('Allocation failed:', error);
      alert('Failed to allocate comps: ' + error.message);
    },
  });

  // Revoke comps mutation
  const revokeMutation = useMutation({
    mutationFn: ({ employeeId, drinks, food }: any) =>
      revokeComps(employeeId, drinks, food),
    onSuccess: () => {
      console.log('Revocation successful, invalidating queries');
      queryClient.invalidateQueries({ queryKey: ['employee-balance'] });
      refetchEmployeesWithBalances();
      setRevokingFrom(null);
      setRevokeForm({ drinks: 0, food: 0 });
      alert('Comps revoked successfully');
    },
    onError: (error: any) => {
      console.error('Revocation failed:', error);
      alert('Failed to revoke comps: ' + error.message);
    },
  });

  const handleEditEmployee = (employee: any) => {
    setEditingEmployee(employee.id);
    setEditForm({
      name: employee.name,
      email: employee.email || '',
      is_admin: employee.is_admin,
      drinks_available: employee.drinks_available || 0,
      food_available: employee.food_available || 0,
    });
  };

  const handleSaveEdit = async () => {
    if (!editingEmployee) return;

    // Get the current employee data
    const currentEmployee = employees.find((e) => e.id === editingEmployee);
    if (!currentEmployee) return;

    // Calculate balance differences
    const drinksDiff = editForm.drinks_available - (currentEmployee.drinks_available || 0);
    const foodDiff = editForm.food_available - (currentEmployee.food_available || 0);

    // Update basic employee info
    const { drinks_available, food_available, ...basicUpdates } = editForm;
    updateMutation.mutate({
      id: editingEmployee,
      updates: basicUpdates,
    });

    // Handle balance changes
    if (drinksDiff !== 0 || foodDiff !== 0) {
      if (drinksDiff > 0 || foodDiff > 0) {
        // Allocate additional comps
        const expiresAt = new Date();
        expiresAt.setDate(expiresAt.getDate() + 30); // Default 30 days
        allocateMutation.mutate({
          employeeId: editingEmployee,
          drinks: Math.max(0, drinksDiff),
          food: Math.max(0, foodDiff),
          expiresAt: expiresAt.toISOString(),
        });
      }
      if (drinksDiff < 0 || foodDiff < 0) {
        // Revoke comps
        revokeMutation.mutate({
          employeeId: editingEmployee,
          drinks: Math.abs(Math.min(0, drinksDiff)),
          food: Math.abs(Math.min(0, foodDiff)),
        });
      }
    }
  };

  const handleCancelEdit = () => {
    setEditingEmployee(null);
    setEditForm({ name: '', email: '', is_admin: false, drinks_available: 0, food_available: 0 });
  };

  const handleResetPIN = (id: string) => {
    if (confirm('Generate a new PIN for this employee?')) {
      resetPinMutation.mutate(id);
    }
  };

  const handleDeleteEmployee = (id: string) => {
    if (confirm('Delete this employee? This cannot be undone.')) {
      deleteMutation.mutate(id);
    }
  };

  const handleOpenAllocate = (employeeId: string) => {
    setAllocatingTo(employeeId);
  };

  const handleAllocateComps = () => {
    if (!allocatingTo) return;

    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + parseInt(allocateForm.expiresIn));

    allocateMutation.mutate({
      employeeId: allocatingTo,
      drinks: allocateForm.drinks,
      food: allocateForm.food,
      expiresAt: expiresAt.toISOString(),
    });
  };

  const handleCloseAllocate = () => {
    setAllocatingTo(null);
    setAllocateForm({ drinks: 2, food: 1, expiresIn: '7' });
  };

  const handleOpenRevoke = (employeeId: string) => {
    setRevokingFrom(employeeId);
  };

  const handleRevokeComps = () => {
    if (!revokingFrom) return;

    if (revokeForm.drinks === 0 && revokeForm.food === 0) {
      alert('Please enter at least one comp type to revoke');
      return;
    }

    if (revokeForm.drinks < 0 || revokeForm.food < 0) {
      alert('Revoke amounts cannot be negative');
      return;
    }

    if (confirm(`Revoke ${revokeForm.drinks} drinks and ${revokeForm.food} food from this employee?`)) {
      revokeMutation.mutate({
        employeeId: revokingFrom,
        drinks: revokeForm.drinks,
        food: revokeForm.food,
      });
    }
  };

  const handleCloseRevoke = () => {
    setRevokingFrom(null);
    setRevokeForm({ drinks: 0, food: 0 });
  };

  // Get balance for an employee
  const getEmployeeBalanceData = async (employeeId: string) => {
    const result = await getEmployeeBalance(employeeId);
    return result.data;
  };

  const formatRelativeTime = (dateString: string) => {
    const now = new Date();
    const then = new Date(dateString);
    const diffMs = now.getTime() - then.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  if (statsLoading || redemptionsLoading || employeesLoading) {
    return (
      <div className={styles.loading}>
        Loading...
      </div>
    );
  }

  return (
    <div className={styles.dashboard}>
      <div className={styles.header}>
        <h1>Dashboard</h1>
        <p className={styles.welcome}>Hi, {admin?.name?.split(' ')[0]}</p>
      </div>

      {/* Main stat - emphasized */}
      <div className={styles.mainStat}>
        <div className={styles.mainStatValue}>{stats?.todayRedemptions || 0}</div>
        <div className={styles.mainStatLabel}>Redemptions Today</div>
      </div>

      {/* Quick actions - more prominent */}
      <div className={styles.quickActions}>
        <button
          onClick={() => navigate('/register')}
          className={styles.primaryButton}
        >
          <span className={styles.buttonIcon}>⊞</span>
          <span>OPEN SCANNER</span>
        </button>
        <button
          onClick={() => navigate('/admin/redemptions')}
          className={styles.secondaryButton}
        >
          <span className={styles.buttonIcon}>📋</span>
          <span>ALL REDEMPTIONS</span>
        </button>
        <button
          onClick={() => navigate('/admin/events')}
          className={styles.secondaryButton}
        >
          <span className={styles.buttonIcon}>📅</span>
          <span>MANAGE EVENTS</span>
        </button>
        <button
          onClick={() => setShowCreateUser(true)}
          className={styles.secondaryButton}
        >
          <span className={styles.buttonIcon}>+</span>
          <span>CREATE EMPLOYEE</span>
        </button>
      </div>

      {/* Employee List */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Employees ({employees.length})</h2>
        {employees.length === 0 ? (
          <div className={styles.emptyState}>
            <p>No employees yet</p>
            <button onClick={() => setShowCreateUser(true)} className={styles.secondaryButton}>
              CREATE FIRST EMPLOYEE
            </button>
          </div>
        ) : (
          <div className={styles.employeeList}>
            {employees.map((employee) => (
              <div key={employee.id} className={styles.employeeItem}>
                {editingEmployee === employee.id ? (
                  // Edit mode
                  <div className={styles.editForm}>
                    <div className={styles.editField}>
                      <label>Name</label>
                      <input
                        type="text"
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        className={styles.editInput}
                      />
                    </div>
                    <div className={styles.editField}>
                      <label>Email</label>
                      <input
                        type="email"
                        value={editForm.email}
                        onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                        className={styles.editInput}
                      />
                    </div>
                    <div className={styles.editField}>
                      <label>
                        <input
                          type="checkbox"
                          checked={editForm.is_admin}
                          onChange={(e) => setEditForm({ ...editForm, is_admin: e.target.checked })}
                        />
                        Admin
                      </label>
                    </div>
                    <div className={styles.balanceEditSection}>
                      <label className={styles.balanceEditLabel}>Current Balance</label>
                      <div className={styles.balanceEditRow}>
                        <div className={styles.balanceEditField}>
                          <label>Drinks</label>
                          <input
                            type="number"
                            min="0"
                            value={editForm.drinks_available}
                            onChange={(e) => setEditForm({ ...editForm, drinks_available: parseInt(e.target.value) || 0 })}
                            className={styles.balanceEditInput}
                          />
                        </div>
                        <div className={styles.balanceEditField}>
                          <label>Food</label>
                          <input
                            type="number"
                            min="0"
                            value={editForm.food_available}
                            onChange={(e) => setEditForm({ ...editForm, food_available: parseInt(e.target.value) || 0 })}
                            className={styles.balanceEditInput}
                          />
                        </div>
                      </div>
                    </div>
                    <div className={styles.editActions}>
                      <button
                        onClick={handleSaveEdit}
                        disabled={updateMutation.isPending}
                        className={styles.saveButton}
                      >
                        SAVE
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className={styles.cancelButton}
                      >
                        CANCEL
                      </button>
                    </div>
                  </div>
                ) : (
                  // View mode
                  <>
                    <div className={styles.employeeInfo}>
                      <span className={styles.employeeName}>
                        {employee.name}
                        {employee.is_admin && <span className={styles.adminBadge}>ADMIN</span>}
                      </span>
                      <span className={styles.employeeEmail}>
                        {employee.email || 'No email'}
                      </span>
                      <span className={styles.employeeBalance}>
                        {employee.drinks_available > 0 && (
                          <span className={styles.balanceItem}>{employee.drinks_available} drinks</span>
                        )}
                        {employee.food_available > 0 && (
                          <span className={styles.balanceItem}>{employee.food_available} food</span>
                        )}
                        {employee.drinks_available === 0 && employee.food_available === 0 && (
                          <span className={styles.balanceEmpty}>No comps</span>
                        )}
                      </span>
                      <span className={styles.employeeEmail} style={{ fontSize: '0.75rem', opacity: 0.5 }}>
                        {employee.last_login_at
                          ? `Last login: ${formatRelativeTime(employee.last_login_at)}`
                          : 'Never logged in'}
                      </span>
                    </div>
                    <div className={styles.employeeActions}>
                      <button
                        onClick={() => handleOpenAllocate(employee.id)}
                        className={styles.primaryActionButton}
                      >
                        ADD COMPS
                      </button>
                      <button
                        onClick={() => handleOpenRevoke(employee.id)}
                        className={styles.revokeButton}
                      >
                        REVOKE COMPS
                      </button>
                      <button
                        onClick={() => handleResetPIN(employee.id)}
                        className={styles.actionButton}
                        disabled={resetPinMutation.isPending}
                      >
                        RESET PIN
                      </button>
                      <button
                        onClick={() => handleEditEmployee(employee)}
                        className={styles.actionButton}
                      >
                        EDIT
                      </button>
                      {!employee.is_admin && (
                        <button
                          onClick={() => handleDeleteEmployee(employee.id)}
                          className={styles.deleteButton}
                          disabled={deleteMutation.isPending}
                        >
                          DELETE
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent activity - simplified */}
      {redemptions.length > 0 && (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Recent Activity</h2>
          <div className={styles.activityList}>
            {redemptions.map((redemption) => (
              <div key={redemption.id} className={styles.activityItem}>
                <div className={styles.activityInfo}>
                  <span className={styles.activityName}>
                    {redemption.employee?.name || 'Unknown'}
                  </span>
                  <span className={styles.activityDetail}>
                    redeemed {redemption.type}
                  </span>
                </div>
                <span className={styles.activityTime}>
                  {formatRelativeTime(redemption.redeemed_at)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {redemptions.length === 0 && (
        <div className={styles.emptyState}>
          <p>No recent activity</p>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateUser && (
        <CreateUserModal
          onClose={() => setShowCreateUser(false)}
          onSuccess={handleUserCreated}
        />
      )}

      {/* Allocate Comps Modal */}
      {allocatingTo && (
        <div className={styles.overlay} onClick={handleCloseAllocate}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>ALLOCATE COMPS</h2>
            </div>
            <div className={styles.allocateForm}>
              <div className={styles.allocateField}>
                <label>Drink Comps</label>
                <input
                  type="number"
                  min="0"
                  value={allocateForm.drinks}
                  onChange={(e) => setAllocateForm({ ...allocateForm, drinks: parseInt(e.target.value) || 0 })}
                  className={styles.allocateInput}
                />
              </div>
              <div className={styles.allocateField}>
                <label>Food Comps</label>
                <input
                  type="number"
                  min="0"
                  value={allocateForm.food}
                  onChange={(e) => setAllocateForm({ ...allocateForm, food: parseInt(e.target.value) || 0 })}
                  className={styles.allocateInput}
                />
              </div>
              <div className={styles.allocateField}>
                <label>Expires In</label>
                <select
                  value={allocateForm.expiresIn}
                  onChange={(e) => setAllocateForm({ ...allocateForm, expiresIn: e.target.value })}
                  className={styles.allocateSelect}
                >
                  <option value="1">1 Day</option>
                  <option value="7">1 Week</option>
                  <option value="30">1 Month</option>
                </select>
              </div>
              <div className={styles.allocateActions}>
                <button
                  onClick={handleCloseAllocate}
                  className={styles.cancelButton}
                  disabled={allocateMutation.isPending}
                >
                  CANCEL
                </button>
                <button
                  onClick={handleAllocateComps}
                  disabled={allocateMutation.isPending}
                  className={styles.saveButton}
                >
                  {allocateMutation.isPending ? 'ALLOCATING...' : 'ALLOCATE'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Revoke Comps Modal */}
      {revokingFrom && (
        <div className={styles.overlay} onClick={handleCloseRevoke}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>REVOKE COMPS</h2>
            </div>
            <div className={styles.allocateForm}>
              {revokingEmployee && (
                <div className={styles.balanceEditSection}>
                  <label className={styles.balanceEditLabel}>Current Balance</label>
                  <div className={styles.balanceEditRow}>
                    <div className={styles.balanceEditField}>
                      <label>Drinks</label>
                      <input
                        type="number"
                        value={revokingEmployee.drinks_available || 0}
                        className={styles.balanceEditInput}
                        readOnly
                      />
                    </div>
                    <div className={styles.balanceEditField}>
                      <label>Food</label>
                      <input
                        type="number"
                        value={revokingEmployee.food_available || 0}
                        className={styles.balanceEditInput}
                        readOnly
                      />
                    </div>
                  </div>
                </div>
              )}
              <div className={styles.allocateField}>
                <label>Drink Comps to Revoke</label>
                <input
                  type="number"
                  min="0"
                  value={revokeForm.drinks}
                  onChange={(e) => setRevokeForm({ ...revokeForm, drinks: parseInt(e.target.value) || 0 })}
                  className={styles.allocateInput}
                />
              </div>
              <div className={styles.allocateField}>
                <label>Food Comps to Revoke</label>
                <input
                  type="number"
                  min="0"
                  value={revokeForm.food}
                  onChange={(e) => setRevokeForm({ ...revokeForm, food: parseInt(e.target.value) || 0 })}
                  className={styles.allocateInput}
                />
              </div>
              <div className={styles.allocateActions}>
                <button
                  onClick={() =>
                    setRevokeForm({
                      drinks: revokingEmployee?.drinks_available || 0,
                      food: revokingEmployee?.food_available || 0,
                    })
                  }
                  className={styles.actionButton}
                  disabled={revokeMutation.isPending}
                >
                  REVOKE ALL
                </button>
                <button
                  onClick={handleCloseRevoke}
                  className={styles.cancelButton}
                  disabled={revokeMutation.isPending}
                >
                  CANCEL
                </button>
                <button
                  onClick={handleRevokeComps}
                  disabled={revokeMutation.isPending}
                  className={styles.deleteButton}
                >
                  {revokeMutation.isPending ? 'REVOKING...' : 'REVOKE'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New PIN Modal */}
      {showPin && newPin && (
        <div className={styles.overlay} onClick={() => setShowPin(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>NEW PIN GENERATED</h2>
            </div>
            <div className={styles.pinDisplay}>
              <p className={styles.pinLabel}>Share this new PIN with the employee:</p>
              <div className={styles.pinValue}>{newPin}</div>
            </div>
            <div className={styles.modalActions}>
              <button onClick={() => setShowPin(false)} className={styles.modalButton}>
                DONE
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
// Force rebuild $(date)
